import { useEffect, useState } from 'react'
import { useAdmin } from '@/hooks/useAdmin'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Building2, Users, Plug, Calendar } from 'lucide-react'

interface OverviewData {
  counts: { organizations: number; users: number; connections: number }
  tierDistribution: Record<string, number>
  recentOrganizations: Array<{ id: string; name: string; tier: string; created_at: string }>
}

export function DashboardPage() {
  const { getOverview } = useAdmin()
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getOverview()
      .then(setData)
      .catch(() => setError('Failed to load overview data'))
      .finally(() => setLoading(false))
  }, [getOverview])

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="brand-wordmark text-2xl">Admin Dashboard</h1>
          <p className="kicker text-muted-foreground mt-1">Platform overview</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-8 w-24 bg-muted/40 rounded animate-pulse mb-2" />
                <div className="h-4 w-16 bg-muted/30 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <h1 className="brand-wordmark text-2xl">Admin Dashboard</h1>
        <Card>
          <CardContent className="p-6 text-destructive text-sm">
            {error || 'No data available'}
          </CardContent>
        </Card>
      </div>
    )
  }

  const kpis = [
    { label: 'Organizations', value: data.counts.organizations, icon: Building2 },
    { label: 'Users', value: data.counts.users, icon: Users },
    { label: 'Connections', value: data.counts.connections, icon: Plug },
  ]

  const tierColors: Record<string, string> = {
    free: 'text-muted-foreground',
    team: 'text-primary',
    scale: 'text-primary',
    enterprise: 'text-primary',
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="brand-wordmark text-2xl">Admin Dashboard</h1>
        <p className="kicker text-muted-foreground mt-1">Platform overview</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="stat-card-glow">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold leading-none">{value.toLocaleString()}</p>
                <p className="kicker text-muted-foreground mt-1">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tier distribution */}
      {Object.keys(data.tierDistribution).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(data.tierDistribution).map(([tier, count]) => (
                <div key={tier} className="flex items-center gap-2">
                  <span
                    className={`kicker uppercase ${tierColors[tier] ?? 'text-foreground'}`}
                  >
                    {tier}
                  </span>
                  <span className="text-sm font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent organizations */}
      {data.recentOrganizations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Organizations</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {data.recentOrganizations.map((org) => (
                <div key={org.id} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="text-sm font-medium">{org.name}</p>
                    <p className="mono-accent text-muted-foreground">{org.id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="status-pill status-pill-muted uppercase">{org.tier}</span>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span className="mono-accent">
                        {new Date(org.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
